import { createContext, use } from 'react';
import type { ReactNode } from 'react';
import { supabase } from '../../shared/supabase/client';

/**
 * The six branding columns of one instance, resolved from its slug *before*
 * anyone signs in (coach-instance, "The client app knows the instance before
 * sign-in"). This is the only thing an unauthenticated visitor may learn, and
 * `public.get_public_instance` is the only way to learn it.
 */
export interface PublicInstance {
  slug: string;
  displayName: string;
  wordmarkText: string | null;
  logoUrl: string | null;
  coachDisplayName: string;
  roleLine: string;
}

/**
 * Resolve a slug, or return `null` when there is no active instance under it.
 *
 * `null` is the *only* signal: an unknown slug, a suspended instance and an
 * instance that exists but is not yours must all be indistinguishable from
 * outside, because `access-and-consent` requires "no hint that the content
 * exists".
 */
export async function loadPublicInstance(slug: string): Promise<PublicInstance | null> {
  const { data, error } = await supabase.rpc('get_public_instance', { p_slug: slug });

  if (error) throw error;

  const row = data.at(0);
  if (!row) return null;

  return {
    slug: row.slug,
    displayName: row.display_name,
    wordmarkText: row.wordmark_text,
    logoUrl: row.logo_url,
    coachDisplayName: row.coach_display_name,
    roleLine: row.role_line,
  };
}

const InstanceContext = createContext<PublicInstance | null>(null);

export function InstanceProvider({
  instance,
  children,
}: {
  instance: PublicInstance;
  children: ReactNode;
}) {
  return <InstanceContext value={instance}>{children}</InstanceContext>;
}

/** The instance the current surface belongs to. Throws outside a provider. */
export function useInstance(): PublicInstance {
  const instance = use(InstanceContext);
  if (!instance) {
    throw new Error('useInstance() was called outside an <InstanceProvider>.');
  }
  return instance;
}
